import type { MapLocation } from './types';

function isFilled(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  if (typeof value === 'number') {
    return !Number.isNaN(value);
  }
  return false;
}

function hasDetailFields(loc: MapLocation): boolean {
  if (isFilled(loc.typeLabel)) {
    return true;
  }
  if (isFilled(loc.email)) {
    return true;
  }
  if (isFilled(loc.participants_quantity)) {
    return true;
  }
  if (isFilled(loc.year)) {
    return true;
  }
  if (loc.links.length > 0) {
    return true;
  }
  if (isFilled(loc.wysiwyg)) {
    return true;
  }
  return false;
}

function prepareWysiwygLinks(container: HTMLElement): void {
  container.querySelectorAll('a[href]').forEach((node) => {
    if (!(node instanceof HTMLAnchorElement)) {
      return;
    }
    node.target = '_blank';
    node.rel = 'noopener noreferrer';
  });
}

function appendWysiwyg(container: HTMLElement, html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'kfx-map-popup__wysiwyg';
  wrap.innerHTML = html;
  prepareWysiwygLinks(wrap);
  container.appendChild(wrap);
}

function appendRow(container: HTMLElement, label: string, value: string): void {
  const row = document.createElement('div');
  row.className = 'kfx-map-popup__row';
  const k = document.createElement('span');
  k.className = 'kfx-map-popup__key';
  k.textContent = `${label}:`;
  const v = document.createElement('span');
  v.className = 'kfx-map-popup__val';
  v.textContent = value;
  row.append(k, v);
  container.appendChild(row);
}

function appendEmailRow(container: HTMLElement, email: string): void {
  const row = document.createElement('div');
  row.className = 'kfx-map-popup__row';
  const k = document.createElement('span');
  k.className = 'kfx-map-popup__key';
  k.textContent = 'E-mail:';
  const v = document.createElement('a');
  v.className = 'kfx-map-popup__val kfx-map-popup__val--link';
  v.href = `mailto:${email.trim()}`;
  v.textContent = email;
  row.append(k, v);
  container.appendChild(row);
}

function appendLinks(container: HTMLElement, loc: MapLocation): void {
  if (loc.links.length === 0) {
    return;
  }
  const wrap = document.createElement('div');
  wrap.className = 'kfx-map-popup__links';
  const title = document.createElement('div');
  title.className = 'kfx-map-popup__links-title';
  title.textContent = 'Linki';
  wrap.appendChild(title);
  const ul = document.createElement('ul');
  ul.className = 'kfx-map-popup__links-list';
  for (const link of loc.links) {
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = link.url;
    a.textContent = link.title || link.url;
    // Map popover: always open in a new tab (ignore ACF target for consistent UX).
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    li.appendChild(a);
    ul.appendChild(li);
  }
  wrap.appendChild(ul);
  container.appendChild(wrap);
}

function fillBody(panel: HTMLElement, loc: MapLocation): void {
  panel.textContent = '';
  if (!hasDetailFields(loc)) {
    const empty = document.createElement('p');
    empty.className = 'kfx-map-popup__empty';
    empty.textContent = 'Brak informacji';
    panel.appendChild(empty);
    return;
  }
  if (isFilled(loc.typeLabel)) {
    appendRow(panel, 'Typ', String(loc.typeLabel));
  }
  if (isFilled(loc.email)) {
    appendEmailRow(panel, String(loc.email));
  }
  if (isFilled(loc.participants_quantity)) {
    appendRow(panel, 'Liczba radnych', String(loc.participants_quantity));
  }
  if (isFilled(loc.year)) {
    appendRow(panel, 'Rok założenia', String(loc.year));
  }
  appendLinks(panel, loc);
  if (isFilled(loc.wysiwyg)) {
    appendWysiwyg(panel, String(loc.wysiwyg));
  }
}

function createPopupHeaderBar(title: string): HTMLElement {
  const bar = document.createElement('div');
  bar.className = 'kfx-map-popup__header-bar';
  const heading = document.createElement('h2');
  heading.className = 'kfx-map-popup__title';
  heading.textContent = title;
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'kfx-map-popup__close';
  close.setAttribute('aria-label', 'Zamknij');
  close.textContent = '×';
  bar.append(heading, close);
  return bar;
}

function createSingleBlock(loc: MapLocation): HTMLElement {
  const block = document.createElement('div');
  block.className = 'kfx-map-accordion kfx-map-accordion--single';
  const panel = document.createElement('div');
  panel.className = 'kfx-map-accordion__panel';
  fillBody(panel, loc);
  block.append(panel);
  return block;
}

function createMultiItem(loc: MapLocation): HTMLElement {
  const details = document.createElement('details');
  details.className = 'kfx-map-accordion kfx-map-accordion--multi';
  const summary = document.createElement('summary');
  summary.className = 'kfx-map-accordion__header kfx-map-accordion__summary';
  summary.textContent = loc.name || '—';
  const panel = document.createElement('div');
  panel.className = 'kfx-map-accordion__panel';
  fillBody(panel, loc);
  details.append(summary, panel);
  details.addEventListener('toggle', () => {
    if (!details.open) {
      return;
    }
    const parent = details.parentElement;
    if (!parent) {
      return;
    }
    parent.querySelectorAll('details.kfx-map-accordion--multi').forEach((other) => {
      if (other !== details) {
        (other as HTMLDetailsElement).open = false;
      }
    });
  });
  return details;
}

export function buildPopupContent(locations: MapLocation[]): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'kfx-map-popup';
  const headerTitle =
    locations.length === 1
      ? locations[0]!.name || '—'
      : `${locations.length} lokalizacje w tym miejscu`;
  wrap.appendChild(createPopupHeaderBar(headerTitle));
  if (locations.length === 1) {
    wrap.appendChild(createSingleBlock(locations[0]!));
    return wrap;
  }
  const list = document.createElement('div');
  list.className = 'kfx-map-popup__list';
  for (const loc of locations) {
    list.appendChild(createMultiItem(loc));
  }
  wrap.appendChild(list);
  return wrap;
}
